import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Info } from '@mui/icons-material'
import { Stack, Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import NormalText from 'common/Typography/NormalText'
import { format, fromUnixTime } from 'date-fns'
import OrbitSession from 'hooks/useCookies'
import {
  GetAllFaberUsersDetails,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
} from 'types/api/faber/oneTimePack.type'

export default function TerminatedCustomerPlans({
  isExternal
}: TerminatedCustomerProps) {
  const location = useLocation()
  const customerInfo = location.state as GetAllFaberUsersDetails
  if (customerInfo?._id === undefined) {
    customerInfo._id = customerInfo.user_id
  }
  const terminatedPlansColumns = [
    {
      field: 'packageName',
      headerName: 'Package Name',
      sortable: false,
      minWidth: 100,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => {
        return (
          <>
            <NormalText>{params.row.packageName}</NormalText>
            <Tooltip
              title={`Applied Plan Id: ${params.row.appliedPlanId}`}
              arrow
            >
              <Info sx={{ color: 'lightgrey', fontSize: 'small' }} />
            </Tooltip>
          </>
        )
      }
    },
    {
      field: 'createdAt',
      headerName: 'Date purchased',
      sortable: false,
      minWidth: 80,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => {
        return (
          <NormalText>
            {format(
              fromUnixTime(params.row.createdAt / 1000),
              'dd MMM yyyy HH:mm'
            )}
          </NormalText>
        )
      }
    },
    {
      field: 'packageDataAllowed',
      headerName: 'Data allocated',
      sortable: false,
      minWidth: 50,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => {
        return <NormalText>{params.row.packageDataAllowed} GB</NormalText>
      }
    },
    {
      field: 'planStatus',
      headerName: 'Pack Status',
      sortable: false,
      minWidth: 50
    },
    {
      field: 'updatedAt',
      headerName: 'Date terminated (Accurate only from 26Sep onwards)',
      sortable: false,
      minWidth: 320,
      renderCell: (params: {
        row: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
      }) => {
        return (
          <>
            <NormalText>
              {format(
                fromUnixTime(params.row.updatedAt / 1000),
                'dd MMM yyyy HH:mm'
              )}
            </NormalText>{' '}
          </>
        )
      }
    }
  ]

  const faberToken = OrbitSession.get('faberToken')

  const [allTerminatedUserPlans, setAllTerminatedUserPlans] = useState<
    GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  >([])

  const {
    isFetching: isFetchingTerminatedOtpackUserPlans,
    refetch: refetchTerminatedOtpackUserPlans
  } = useQuery(
    ['getAllTerminatedOtpackUserPlans', customerInfo._id],
    async () => {
      return (isExternal ? 
      OneTimePackApi.getExternalAllTerminatedOtpackUserPlans(
        customerInfo._id,
        faberToken
      ) :
      OneTimePackApi.getAllTerminatedOtpackUserPlans(
        customerInfo._id,
        faberToken
      ))
    },
    {
      onSuccess: (res) => {
        setAllTerminatedUserPlans(res.data.body.data)
      },
      onError: () => {
        return []
      }
    }
  )

  useEffect(() => {
    refetchTerminatedOtpackUserPlans()
  }, [location])

  return (
    <Stack>
      <NormalText isBold sx={{ fontSize: '21px' }}>
        Terminated Packs
      </NormalText>
      <NormalText theme='muted'>
        Details of customer&apos;s pack(s) that have been used up.
      </NormalText>

      <MUIDataGrid
        loading={isFetchingTerminatedOtpackUserPlans}
        columns={terminatedPlansColumns}
        rows={allTerminatedUserPlans ? allTerminatedUserPlans : []}
        getRowId={(row) => row.userPlanId}
        disableColumnSelector
        disableSelectionOnClick
      />
    </Stack>
  )
}

interface TerminatedCustomerProps {
  isExternal: boolean
}
